/**
 * ВХОД ПО ССЫЛКЕ С ТОКЕНАМИ.
 *
 * ЧТО ЭТО. Ссылки вида
 *   /?_peopleToken=<uuid>&_cartToken=<uuid>
 * авторизуют пользователя и привязывают к нему корзину без формы входа.
 *
 * КАК. Ровно то же, что делает обычный вход (useAuthLogin →
 * UserStore.getFullUserInfo → history.go(0)): кладём токены в localStorage
 * под ключи `headerToken` и `cartToken` (значения — JSON-строки, их читает
 * getLocalStorageItem через JSON.parse) и перезагружаем страницу. После
 * перезагрузки приложение стартует уже авторизованным: getUser шлёт
 * X-People-Token, а CacheDataRoute привязывает корзину к пользователю
 * (rebindCartPid) — как после любого входа.
 *
 * ПЕРЕЗАГРУЖАЕМСЯ НА ТОТ ЖЕ АДРЕС, НО БЕЗ ТОКЕНОВ — чтобы ссылку с чужой
 * сессией было трудно случайно переслать или сохранить в закладки.
 * Остальные query-параметры и hash сохраняются. Через `location.replace`:
 * адрес с токенами не остаётся в истории вкладки.
 *
 * ДО ИНИЦИАЛИЗАЦИИ ПРИЛОЖЕНИЯ. Модуль работает в момент своего вычисления
 * и импортируется ПЕРВЫМ в браузерных entry (src/client.tsx — Start,
 * src/index.js — легаси SPA). ES-модули вычисляются в порядке импортов,
 * поэтому сторы, читающие localStorage при импорте (CartStore берёт
 * cartToken на module-eval), видят уже новые значения, а entry по флагу
 * `isAuthLinkRedirecting` не запускает ни рендер, ни гидрацию.
 *
 * SSR НЕ ЗАТРАГИВАЕТ: на сервере `window` нет — модуль ничего не делает, и
 * в серверный граф он не входит (импортируют только клиентские entry).
 *
 * Только ES2020/Web API из Safari 14.1 (см. CLAUDE.md): URL, URLSearchParams.
 *
 * Проверки: e2e/tests/auth-link.spec.ts, cabinet-auth-link.spec.ts,
 * auth-link-ssr.spec.ts.
 */

export const AUTH_LINK_PEOPLE_PARAM = '_peopleToken';
export const AUTH_LINK_CART_PARAM = '_cartToken';

/**
 * Ключи localStorage — те же, что пишет UserStore.getFullUserInfo и читают
 * src/api/graphql*.ts (X-People-Token / X-Cart-Token) и CartStore.
 */
export const AUTH_LINK_PEOPLE_STORAGE_KEY = 'headerToken';
export const AUTH_LINK_CART_STORAGE_KEY = 'cartToken';

/**
 * Лог перед перезагрузкой. По нему e2e отличает НАШУ перезагрузку от любой
 * другой навигации — константа общая, разъехаться не может.
 */
export const AUTH_LINK_LOG = '[auth-link] токены из ссылки сохранены, перезагружаемся без них';

export interface AuthLinkTokens {
  peopleToken: string;
  cartToken: string;
}

/** Значения, которые генератор ссылок мог подставить вместо токена. */
const EMPTY_LIKE = ['undefined', 'null'];

const isUsableToken = (value: string): boolean =>
  value !== '' && EMPTY_LIKE.indexOf(value.toLowerCase()) === -1;

/**
 * Токены из query-строки. null — ссылка не наша или невалидна:
 * должны прийти ОБА параметра, каждый ровно один раз и непустой.
 * Посторонние параметры (utm и т.п.) не мешают — они сохраняются.
 */
export const parseAuthLinkTokens = (search: string): AuthLinkTokens | null => {
  const params = new URLSearchParams(search);
  const people = params.getAll(AUTH_LINK_PEOPLE_PARAM);
  const cart = params.getAll(AUTH_LINK_CART_PARAM);

  if (people.length !== 1 || cart.length !== 1) {
    return null;
  }

  const peopleToken = people[0].trim();
  const cartToken = cart[0].trim();

  if (!isUsableToken(peopleToken) || !isUsableToken(cartToken)) {
    return null;
  }

  return { peopleToken, cartToken };
};

/** Тот же адрес (путь, прочие параметры, hash) — без токенов. */
export const stripAuthLinkParams = (href: string): string => {
  const url = new URL(href);
  url.searchParams.delete(AUTH_LINK_PEOPLE_PARAM);
  url.searchParams.delete(AUTH_LINK_CART_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
};

const saveTokens = ({ peopleToken, cartToken }: AuthLinkTokens): void => {
  try {
    window.localStorage.setItem(
      AUTH_LINK_PEOPLE_STORAGE_KEY,
      JSON.stringify(peopleToken)
    );
    window.localStorage.setItem(
      AUTH_LINK_CART_STORAGE_KEY,
      JSON.stringify(cartToken)
    );
  } catch (e) {
    // Хранилище недоступно (приватный режим, квота). Токены сохранить
    // негде, но из адресной строки их всё равно убираем — ниже.
    console.log('[auth-link] не удалось сохранить токены:', e);
  }
};

/** @returns true, если запущена перезагрузка и приложение стартовать не должно. */
const consumeAuthLink = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const tokens = parseAuthLinkTokens(window.location.search);
    if (!tokens) {
      return false;
    }
    saveTokens(tokens);
    console.log(AUTH_LINK_LOG);
    window.location.replace(stripAuthLinkParams(window.location.href));
    return true;
  } catch (e) {
    console.log('[auth-link] ссылка не обработана:', e);
    return false;
  }
};

/**
 * Вычисляется ОДИН раз — при первом импорте модуля, до остального кода
 * entry. true — перезагрузка уже запущена: не рендерить и не гидрировать.
 */
export const isAuthLinkRedirecting: boolean = consumeAuthLink();
